import { useMemo } from "react";
import { useCourseDetailStore } from "@/stores/courseDetailStore";
import { CourseTreeNodeTypeEnum, type CourseTreeNode } from "@/types/generated";

interface SubchapterNavigationContext {
  isFirstInChapter: boolean;
  isLastInChapter: boolean;
  isLastChapterInModule: boolean;
  isLastModule: boolean;
  currentChapter: CourseTreeNode | null;
  currentModule: CourseTreeNode | null;
  nextChapter: CourseTreeNode | null;
  nextModule: CourseTreeNode | null;
  prevSubchapter: CourseTreeNode | null;
  nextSubchapter: CourseTreeNode | null;
}

/**
 * Resolves prev/next subchapter siblings for the selected (or overridden) subchapter.
 * Optional `subchapterId` is used when the route selects a chapter (intro scroll)
 * but navigation should still target the embedded first subchapter.
 */
export const useSubchapterNavigationContext = (
  subchapterId?: string
): SubchapterNavigationContext => {
  const { courseData, selectedNodeId, nodeIndex } = useCourseDetailStore();
  const resolvedId = subchapterId ?? selectedNodeId;

  return useMemo(() => {
    const empty: SubchapterNavigationContext = {
      isFirstInChapter: false,
      isLastInChapter: false,
      isLastChapterInModule: false,
      isLastModule: false,
      currentChapter: null,
      currentModule: null,
      nextChapter: null,
      nextModule: null,
      prevSubchapter: null,
      nextSubchapter: null,
    };

    if (!resolvedId || !courseData?.structure) return empty;

    const entry = nodeIndex.get(resolvedId);
    if (!entry || entry.node.type !== CourseTreeNodeTypeEnum.Subchapter)
      return empty;

    const chapter = entry.chapter;
    const module = entry.module;
    if (!chapter || !module) return empty;

    const subchapters =
      chapter.children?.filter(
        (c) => c.type === CourseTreeNodeTypeEnum.Subchapter
      ) ?? [];
    const subIdx = subchapters.findIndex((s) => s.id === resolvedId);

    const chapters =
      module.children?.filter(
        (c) => c.type === CourseTreeNodeTypeEnum.Chapter
      ) ?? [];
    const chapterIdx = chapters.findIndex((c) => c.id === chapter.id);

    const modules = courseData.structure.filter(
      (n: CourseTreeNode) => n.type === CourseTreeNodeTypeEnum.Module
    );
    const moduleIdx = modules.findIndex(
      (m: CourseTreeNode) => m.id === module.id
    );

    const isLastChapter = chapterIdx === chapters.length - 1;
    const isLastMod = moduleIdx === modules.length - 1;

    const nextChapterNode = !isLastChapter ? chapters[chapterIdx + 1] : null;
    const nextModuleNode = !isLastMod ? modules[moduleIdx + 1] : null;

    return {
      isFirstInChapter: subIdx === 0,
      isLastInChapter: subIdx === subchapters.length - 1,
      isLastChapterInModule: isLastChapter,
      isLastModule: isLastMod,
      currentChapter: chapter,
      currentModule: module,
      nextChapter: nextChapterNode,
      nextModule: nextModuleNode,
      prevSubchapter: subIdx > 0 ? subchapters[subIdx - 1] : null,
      nextSubchapter:
        subIdx < subchapters.length - 1 ? subchapters[subIdx + 1] : null,
    };
  }, [courseData, resolvedId, nodeIndex]);
};
